import type { FuzzyPinyinLevel } from "./constants.js";

const TONE_MAP: Record<string, string> = {
  ā: "a",
  á: "a",
  ǎ: "a",
  à: "a",
  ē: "e",
  é: "e",
  ě: "e",
  è: "e",
  ī: "i",
  í: "i",
  ǐ: "i",
  ì: "i",
  ō: "o",
  ó: "o",
  ǒ: "o",
  ò: "o",
  ū: "u",
  ú: "u",
  ǔ: "u",
  ù: "u",
  ǖ: "v",
  ǘ: "v",
  ǚ: "v",
  ǜ: "v",
  ü: "v",
  ń: "n",
  ň: "n",
  ǹ: "n",
  ḿ: "m",
};

export function stripTones(raw: string): string {
  let out = "";
  for (const ch of raw.trim().toLowerCase()) {
    out += TONE_MAP[ch] ?? ch;
  }
  return out.replace(/[^a-z]/g, "");
}

export function splitPinyinSyllables(raw: string): string[] {
  return raw
    .trim()
    .split(/[\s·•]+/)
    .map(stripTones)
    .filter((s) => s.length > 0);
}

export function syllableInitial(syl: string): string {
  const s = stripTones(syl);
  return s.charAt(0) ?? "";
}

export function abbrFromSyllables(syllables: readonly string[]): string {
  return syllables.map(syllableInitial).join("");
}

const RETROFLEX: Record<string, string> = {
  zh: "z",
  ch: "c",
  sh: "s",
};

function mergeRetroflex(syl: string): string {
  for (const [from, to] of Object.entries(RETROFLEX)) {
    if (syl.startsWith(from)) return to + syl.slice(from.length);
  }
  return syl;
}

function mergeNasal(syl: string): string {
  if (syl.endsWith("iang")) return `${syl.slice(0, -4)}ian`;
  if (syl.endsWith("uang")) return `${syl.slice(0, -4)}uan`;
  if (syl.endsWith("ang")) return `${syl.slice(0, -3)}an`;
  if (syl.endsWith("eng")) return `${syl.slice(0, -3)}en`;
  if (syl.endsWith("ing")) return `${syl.slice(0, -3)}in`;
  if (syl.endsWith("ong")) return syl;
  return syl;
}

export function normalizePinyin(syl: string, level: FuzzyPinyinLevel): string {
  let s = stripTones(syl);
  if (level >= 1) {
    s = mergeNasal(mergeRetroflex(s));
  }
  return s;
}

export function pinyinSetsIntersect(
  a: readonly string[],
  b: readonly string[],
  level: FuzzyPinyinLevel,
): boolean {
  const bset = new Set(b.map((x) => normalizePinyin(x, level)));
  for (const x of a) {
    if (bset.has(normalizePinyin(x, level))) return true;
  }
  return false;
}

/**
 * Input-side matching: abbreviation (initials) plus optional full-pinyin syllables.
 * Fuzzy initials (zh/z etc.) are already merged because abbr uses the first letter.
 * Full-pinyin query may include fuzzy nasal/retroflex when `fuzzyInput` is true.
 */
export function matchAbbrQuery(query: string, abbrs: readonly string[]): boolean {
  const q = query.trim().toLowerCase().replace(/[^a-z]/g, "");
  if (!q) return false;
  return abbrs.some((a) => a === q || fuzzyAbbrEqual(a, q));
}

function fuzzyAbbrEqual(a: string, b: string): boolean {
  if (a === b) return true;
  if (a.length !== b.length) {
    return expandFullPinyinQuery(b).includes(a);
  }
  return false;
}

const FULL_SYL_SPLIT = /(?:zh|ch|sh|[bpmfdtnlgkhjqxzcsryw]?h?)(?:iang|iong|uang|ueng|ang|eng|ing|ong|ian|iao|uai|uan|ai|ei|ao|ou|er|an|en|in|un|üan|üe|ia|ie|ua|uo|ue|iu|ui|ve|a|o|e|i|u|v)/g;

export function expandFullPinyinQuery(query: string): string[] {
  const q = query.trim().toLowerCase().replace(/[^a-z]/g, "");
  if (q.length === 4 && [...q].every((c) => c >= "a" && c <= "z")) {
    return [q];
  }
  const syls = q.match(FULL_SYL_SPLIT);
  if (syls && syls.join("") === q && syls.length === 4) {
    return [abbrFromSyllables(syls)];
  }
  return [q];
}

export function queryMatchesIdiom(
  query: string,
  abbrs: readonly string[],
  pinyin: readonly string[],
): boolean {
  const q = query.trim().toLowerCase().replace(/[^a-z]/g, "");
  if (abbrs.includes(q)) return true;
  if (matchAbbrQuery(q, abbrs)) return true;

  const joined = pinyin.join("");
  if (q === joined) return true;

  const fuzzyJoined = pinyin.map((s) => normalizePinyin(s, 1)).join("");
  if (q === fuzzyJoined) return true;

  const qSyls = splitQueryToFour(q);
  if (qSyls) {
    const exact = qSyls.every((s, i) => s === (pinyin[i] ?? "") || s === syllableInitial(pinyin[i] ?? ""));
    if (exact) return true;
    const fuzzy = qSyls.every((s, i) => {
      const target = pinyin[i] ?? "";
      return (
        normalizePinyin(s, 1) === normalizePinyin(target, 1) ||
        s === syllableInitial(target)
      );
    });
    if (fuzzy) return true;
  }
  return false;
}

function splitQueryToFour(q: string): string[] | null {
  if (q.length < 4) return null;
  if (q.length === 4) return [...q];
  const syls = q.match(FULL_SYL_SPLIT);
  if (syls && syls.length === 4 && syls.join("") === q) return syls;
  return null;
}

export function countAbbrSyllables(query: string): number {
  const q = query.trim().toLowerCase().replace(/[^a-z]/g, "");
  if (q.length < 4) return q.length;
  if (q.length === 4) return 4;
  const syls = q.match(FULL_SYL_SPLIT);
  if (syls && syls.join("") === q) return syls.length;
  return Math.min(4, q.length);
}

/** Common polyphone extras keyed by character, used when building first/last sets. */
export const POLYPHONE_EXTRA: Record<string, string[]> = {
  行: ["xing", "hang"],
  重: ["zhong", "chong"],
  乐: ["le", "yue"],
  长: ["chang", "zhang"],
  着: ["zhe", "zhao", "zhuo"],
  差: ["cha", "chai", "ci"],
  和: ["he", "huo", "hu"],
  降: ["jiang", "xiang"],
  弹: ["dan", "tan"],
  调: ["diao", "tiao"],
  省: ["sheng", "xing"],
  数: ["shu", "shuo"],
  传: ["chuan", "zhuan"],
  藏: ["cang", "zang"],
  朝: ["chao", "zhao"],
  盛: ["sheng", "cheng"],
  给: ["gei", "ji"],
  觉: ["jue", "jiao"],
  空: ["kong"],
  少: ["shao"],
  便: ["bian", "pian"],
  将: ["jiang"],
  间: ["jian"],
  参: ["can", "shen", "cen"],
  度: ["du", "duo"],
  得: ["de", "dei"],
  地: ["di", "de"],
  都: ["dou", "du"],
  干: ["gan"],
  还: ["hai", "huan"],
  好: ["hao"],
  会: ["hui", "kuai"],
  教: ["jiao"],
  看: ["kan"],
  了: ["le", "liao"],
  吗: ["ma"],
  呢: ["ne"],
  铺: ["pu"],
  强: ["qiang", "jiang"],
  散: ["san", "san"],
  丧: ["sang"],
  色: ["se", "shai"],
  塞: ["sai", "se", "sai"],
  折: ["zhe", "she"],
  正: ["zheng"],
  只: ["zhi"],
  中: ["zhong"],
  种: ["zhong", "chong"],
  爪: ["zhao", "zhua"],
  转: ["zhuan", "zhuan"],
  石: ["shi", "dan"],
  宿: ["su", "xiu", "xiu"],
  血: ["xue", "xie"],
  削: ["xue", "xiao"],
  吓: ["xia", "he"],
  巷: ["xiang", "hang"],
  兴: ["xing"],
  咽: ["yan", "ye"],
  艾: ["ai", "yi"],
  薄: ["bao", "bo"],
  伯: ["bo", "bai"],
  扁: ["bian", "pian"],
  屏: ["ping", "bing"],
  卜: ["bu", "bo"],
  采: ["cai"],
  曾: ["ceng", "zeng"],
  颤: ["chan", "zhan"],
  称: ["cheng", "chen"],
  澄: ["cheng", "deng"],
  匙: ["chi", "shi"],
  臭: ["chou", "xiu"],
  处: ["chu"],
  畜: ["chu", "xu"],
  创: ["chuang"],
  大: ["da", "dai"],
  待: ["dai"],
  当: ["dang"],
  倒: ["dao"],
  提: ["ti", "di"],
  读: ["du", "dou"],
  恶: ["e", "wu", "e"],
  发: ["fa"],
  分: ["fen"],
  缝: ["feng"],
  服: ["fu"],
  杆: ["gan"],
  膏: ["gao"],
  供: ["gong"],
  冠: ["guan"],
  汗: ["han"],
  号: ["hao"],
  喝: ["he", "ye"],
  横: ["heng"],
  哄: ["hong"],
  红: ["hong", "gong"],
  划: ["hua"],
  晃: ["huang"],
  混: ["hun"],
  几: ["ji"],
  济: ["ji"],
  夹: ["jia"],
  假: ["jia"],
  监: ["jian"],
  结: ["jie"],
  解: ["jie", "xie"],
  禁: ["jin"],
  尽: ["jin"],
  劲: ["jin", "jing"],
  据: ["ju"],
  卷: ["juan"],
  卡: ["ka", "qia"],
  壳: ["ke", "qiao"],
  拉: ["la"],
  烙: ["lao", "luo"],
  勒: ["le", "lei"],
  累: ["lei"],
  量: ["liang"],
  淋: ["lin"],
  令: ["ling"],
  溜: ["liu"],
  笼: ["long"],
  弄: ["nong", "long"],
  露: ["lu", "lou"],
  绿: ["lv", "lu"],
  落: ["luo", "lao", "la"],
  脉: ["mai", "mo"],
  埋: ["mai", "man"],
  蔓: ["man", "wan"],
  蒙: ["meng"],
  秘: ["mi", "bi"],
  模: ["mo", "mu"],
  难: ["nan"],
  宁: ["ning"],
  胖: ["pang", "pan"],
  刨: ["pao", "bao"],
  炮: ["pao", "bao"],
  喷: ["pen"],
  劈: ["pi"],
  仆: ["pu"],
  奇: ["qi", "ji"],
  乾: ["qian", "gan"],
  亲: ["qin", "qing"],
  曲: ["qu"],
  圈: ["quan", "juan"],
  扫: ["sao"],
  煞: ["sha"],
  扇: ["shan"],
  上: ["shang"],
  稍: ["shao"],
  舍: ["she"],
  什: ["shen", "shi"],
  识: ["shi", "zhi"],
  似: ["si", "shi"],
  熟: ["shu", "shou"],
  术: ["shu", "zhu"],
  说: ["shuo", "shui", "yue"],
  缩: ["suo"],
  踏: ["ta"],
  苔: ["tai"],
  倘: ["tang"],
  挑: ["tiao"],
  帖: ["tie"],
  通: ["tong"],
  同: ["tong"],
  吐: ["tu"],
  拓: ["tuo", "ta"],
  为: ["wei"],
  尾: ["wei", "yi"],
  委: ["wei"],
  鲜: ["xian"],
  相: ["xiang"],
  校: ["xiao", "jiao"],
  芯: ["xin"],
  旋: ["xuan"],
  哑: ["ya"],
  要: ["yao"],
  钥: ["yao", "yue"],
  遗: ["yi", "wei"],
  饮: ["yin"],
  应: ["ying"],
  佣: ["yong"],
  与: ["yu"],
  语: ["yu"],
  晕: ["yun"],
  载: ["zai"],
  脏: ["zang"],
  择: ["ze", "zhai"],
  扎: ["zha", "za"],
  轧: ["zha", "ya", "ga"],
  粘: ["zhan", "nian"],
  涨: ["zhang"],
  症: ["zheng"],
  轴: ["zhou"],
  属: ["shu", "zhu"],
  幢: ["zhuang", "chuang"],
  兹: ["zi", "ci"],
  钻: ["zuan"],
};

export function extraReadingsForChar(ch: string, existing: readonly string[]): string[] {
  const extras = POLYPHONE_EXTRA[ch];
  if (!extras) return [];
  const have = new Set(existing);
  return extras.filter((x) => !have.has(x));
}
